import { useInfiniteQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api, type ContactBody, type ContactEvent } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/primitives";
import { FilterLink, LogTable, type AuditSearch, type SetSearch } from "./AuditLogPage";

// The audit log's "Contacts" tab (docs/scheduled-reports.md, "External contacts"): every change to
// external contacts and approved domains. The domain list itself is on the Contacts page.

const KINDS: Record<ContactEvent["kind"], { label: string; tone: "neutral" | "info" | "warn" | "accent" | "good" }> = {
  contact_added: { label: "Contact added", tone: "good" },
  contact_changed: { label: "Contact changed", tone: "accent" },
  contact_deleted: { label: "Contact deleted", tone: "warn" },
  domain_added: { label: "Domain approved", tone: "info" },
  domain_removed: { label: "Domain removed", tone: "warn" },
};

export function ContactLog({ search, set }: { search: AuditSearch; set: SetSearch }) {
  const results = useInfiniteQuery({
    queryKey: ["contact-events", search.contact],
    queryFn: ({ pageParam }) => api.contactEvents(search.contact, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    retry: false,
  });
  return (
    <>
      {search.contact && (
        <Badge tone="accent" className="mb-3 h-8 px-2 text-xs">
          One contact · {search.contact.slice(0, 8)}
          <button onClick={() => set({ contact: undefined })} aria-label="Show every contact" className="rounded hover:bg-accent/10"><X className="size-3" /></button>
        </Badge>
      )}
      <LogTable
        results={results}
        items={results.data?.pages.flatMap((p) => p.events) ?? []}
        headers={["When", "By", "Change", "Contact or domain", "Detail"]}
        empty="No contacts or approved domains have been changed yet."
        row={(e) => <ContactEventRow key={e.id} e={e} onFilter={set} />}
      />
    </>
  );
}

function ContactEventRow({ e, onFilter }: { e: ContactEvent; onFilter: SetSearch }) {
  const k = KINDS[e.kind];
  const cell = "border-b border-border px-3 py-2 align-top";
  const d = e.detail;
  return (
    <tr className="hover:bg-surface-2/60">
      <td className={cn(cell, "whitespace-nowrap tabular text-muted")} title={e.at}>
        {new Date(e.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" })}
      </td>
      <td className={cell}>{e.actorEmail ?? e.actorId}</td>
      <td className={cn(cell, "whitespace-nowrap")}><Badge tone={k.tone}>{k.label}</Badge></td>
      <td className={cell}>
        {e.contactId ? (
          <FilterLink onClick={() => onFilter({ contact: e.contactId! })} title="Only this contact">{d?.name ?? d?.email ?? e.contactId}</FilterLink>
        ) : <span className="font-mono text-xs">{e.domain}</span>}
        {e.contactId && d?.name && <span className="block text-xs text-subtle">{d.email}</span>}
      </td>
      <td className={cn(cell, "min-w-64 text-muted")}>{d ? <Change d={d} /> : <span className="text-subtle">—</span>}</td>
    </tr>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** A contact's locations, or for a change, what changed. */
function Change({ d }: { d: ContactBody & { before?: ContactBody } }) {
  const b = d.before;
  if (!b) return <span title={d.locationIds.join(", ")}>{plural(d.locationIds.length, "location")}</span>;
  const added = d.locationIds.filter((id) => !b.locationIds.includes(id));
  const removed = b.locationIds.filter((id) => !d.locationIds.includes(id));
  const parts = [
    b.email !== d.email && `email ${b.email} → ${d.email}`,
    b.name !== d.name && `name ${b.name ?? "none"} → ${d.name ?? "none"}`,
    added.length && `added ${added.map((id) => `#${id}`).join(", ")}`,
    removed.length && `removed ${removed.map((id) => `#${id}`).join(", ")}`,
  ].filter(Boolean);
  return <span>{parts.join(" · ") || "No change"}</span>;
}
